import React, { useEffect, useState } from "react";
import { GoogleLogo, Sparkle, X, WarningCircle, EyeSlash, Eye } from "@phosphor-icons/react";

/**
 * MEX Magic hibrid belépési modal.
 *
 * Módok:
 *   - login    : Google + email/jelszó
 *   - register : email + jelszó + jelszó megerősítés (csak meghívott)
 *   - forgot   : email → reset link kérés
 *   - reset    : reset_token + új jelszó
 *   - denied   : allowlist-elutasítás (Google-flowból)
 *
 * A modal ugyanazt a session_token cookie mechanizmust használja, mint
 * a Google-flow (backend beállítja set_cookie-val a válaszban).
 */

const API = process.env.REACT_APP_BACKEND_URL;

// FastAPI 422 detail lehet string/array/obj — normalizáljuk stringre.
function formatApiErrorDetail(detail, fallback = "Valami hiba történt. Kérlek próbáld újra.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.detail === "string") return detail.detail;
  return String(detail);
}

async function apiPost(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* ignore */ }
  if (!res.ok) {
    const detail = data?.detail ?? `HTTP ${res.status}`;
    const err = new Error(formatApiErrorDetail(detail));
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return data;
}

export default function LoginModal({
  open,
  onClose,
  denied = null,
  initialMode = "login",
  initialEmail = "",
  resetToken = null,
}) {
  const [mode, setMode] = useState(resetToken ? "reset" : initialMode);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Reset the internal state minden nyitáskor.
  useEffect(() => {
    if (!open) return;
    setMode(resetToken ? "reset" : (initialMode || "login"));
    setFirstName("");
    setEmail(initialEmail || "");
    setPassword("");
    setConfirmPassword("");
    setError("");
    setInfo("");
    setSubmitting(false);
    setShowPwd(false);
  }, [open, initialMode, initialEmail, resetToken]);

  // ESC-re bezár + body scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const expectedEmail = (email || initialEmail || "").trim().toLowerCase();
    const redirectUrl = `${window.location.origin}/dashboard${
      expectedEmail ? `?expected_email=${encodeURIComponent(expectedEmail)}` : ""
    }`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const goDashboard = () => {
    // Sikeres belépés után hard reload — AuthContext friss /auth/me hívást ad.
    window.location.href = "/dashboard";
  };

  async function submit(e) {
    e?.preventDefault?.();
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      if (mode === "login") {
        await apiPost("/api/auth/password-login", { email: email.trim().toLowerCase(), password });
        goDashboard();
        return;
      }
      if (mode === "register") {
        if (!firstName.trim()) {
          throw new Error("Kérlek add meg a keresztneved.");
        }
        if (password !== confirmPassword) {
          throw new Error("A két jelszó nem egyezik.");
        }
        await apiPost("/api/auth/register", {
          first_name: firstName.trim(),
          email: email.trim().toLowerCase(),
          password,
          confirm_password: confirmPassword,
        });
        goDashboard();
        return;
      }
      if (mode === "forgot") {
        const r = await apiPost("/api/auth/password-reset/request", { email: email.trim().toLowerCase() });
        setInfo(r?.message || "Ha ehhez az e-mail címhez tartozik aktív hozzáférés, elküldtük a jelszó-visszaállító linket.");
        return;
      }
      if (mode === "reset") {
        if (password !== confirmPassword) {
          throw new Error("A két jelszó nem egyezik.");
        }
        await apiPost("/api/auth/password-reset/confirm", { token: resetToken, password });
        goDashboard();
        return;
      }
    } catch (err) {
      const detail = err?.detail;
      if (mode === "login" && detail && typeof detail === "object" && detail.code === "registration_required") {
        setEmail((detail.email || email || "").trim().toLowerCase());
        setMode("register");
        setInfo(detail.message || "Ehhez az email címhez még nem készült jelszavas fiók. Regisztrálj, vagy folytasd Google-lel.");
        setError("");
        return;
      }
      if (mode === "register" && detail && typeof detail === "object" && detail.code === "account_exists") {
        setEmail((detail.email || email || "").trim().toLowerCase());
        setMode("login");
        setInfo(detail.message || "Ehhez az email címhez már tartozik fiók. Kérlek, jelentkezz be.");
        setError("");
        return;
      }
      setError(err.message || "Hiba történt.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="login-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 overflow-y-auto"
      style={{
        background: "rgba(40,28,80,0.36)",
        backdropFilter: "blur(14px) saturate(120%)",
        WebkitBackdropFilter: "blur(14px) saturate(120%)",
        animation: "lm-overlay-in 240ms ease-out",
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <style>{`
        @keyframes lm-overlay-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lm-card-in {
          from { opacity: 0; transform: translateY(14px) scale(.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        data-testid="login-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        className="relative w-full max-w-[460px] rounded-[28px] overflow-hidden my-auto"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(252,249,255,0.99) 100%)",
          border: "1px solid rgba(232,228,250,0.95)",
          boxShadow: "0 40px 100px -20px rgba(60,52,137,0.32), 0 16px 40px -10px rgba(60,52,137,0.18), 0 1px 0 rgba(255,255,255,0.85) inset",
          animation: "lm-card-in 280ms cubic-bezier(0.19,1,0.22,1)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="login-modal-close"
          aria-label="Bezárás"
          className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center transition z-10"
          style={{ color: "#7B7596", background: "rgba(94,80,247,0.06)" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(94,80,247,0.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(94,80,247,0.06)")}
        >
          <X size={16} weight="bold" />
        </button>

        {/* Ambient glow */}
        <div aria-hidden="true" className="absolute -top-32 -right-20 w-[260px] h-[260px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, rgba(94,80,247,0.20) 0%, rgba(94,80,247,0) 70%)", filter: "blur(20px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 -left-20 w-[240px] h-[240px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, rgba(41,190,218,0.16) 0%, rgba(41,190,218,0) 72%)", filter: "blur(24px)" }} />

        <div className="relative px-9 pt-10 pb-9 sm:px-11 sm:pt-12 sm:pb-10">
          <BrandRow />

          {denied ? (
            <DeniedBlock denied={denied} onRetry={() => onClose?.()} />
          ) : mode === "reset" ? (
            <ResetForm
              password={password} setPassword={setPassword}
              confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
              showPwd={showPwd} setShowPwd={setShowPwd}
              submit={submit} submitting={submitting}
              error={error} info={info}
            />
          ) : mode === "register" ? (
            <RegisterForm
              firstName={firstName} setFirstName={setFirstName}
              email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
              showPwd={showPwd} setShowPwd={setShowPwd}
              submit={submit} submitting={submitting}
              error={error}
              info={info}
              onGoogle={handleGoogleLogin}
              goLogin={() => { setMode("login"); setError(""); setInfo(""); }}
            />
          ) : mode === "forgot" ? (
            <ForgotForm
              email={email} setEmail={setEmail}
              submit={submit} submitting={submitting}
              error={error} info={info}
              goLogin={() => { setMode("login"); setError(""); setInfo(""); }}
            />
          ) : (
            <LoginForm
              email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              showPwd={showPwd} setShowPwd={setShowPwd}
              submit={submit} submitting={submitting}
              error={error}
              info={info}
              onGoogle={handleGoogleLogin}
              goRegister={() => { setMode("register"); setError(""); setInfo(""); }}
              goForgot={() => { setMode("forgot"); setError(""); setInfo(""); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── SUBCOMPONENTS ─────────────────────────── */

function BrandRow() {
  return (
    <div className="flex items-center gap-3 mb-6" data-testid="login-modal-logo">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-[0_12px_32px_rgba(94,80,247,0.36)]" style={{ background: "linear-gradient(135deg, #5e50f7 0%, #3a2fd3 100%)" }}>
        <Sparkle size={24} weight="fill" color="#fff" />
      </div>
      <div className="font-heading text-[24px] tracking-tight text-[#2B2545] font-bold">
        MEX <span className="text-[#5e50f7]">Magic</span>
      </div>
    </div>
  );
}

const inputCls = "w-full px-4 py-[13px] rounded-xl bg-[#FBF9FF] border border-[#E5E0F5] text-[#1B1733] placeholder-[#9A93B6] outline-none transition focus:border-[#5e50f7] focus:ring-4 focus:ring-[#5e50f7]/12";

function EmailInput({ value, onChange, testId, autoFocus = false, disabled = false }) {
  return (
    <input
      type="email"
      autoComplete="email"
      required
      autoFocus={autoFocus}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Email cím"
      className={inputCls}
      style={{ fontSize: 15 }}
      data-testid={testId}
    />
  );
}

function PasswordInput({ value, onChange, testId, autoComplete = "current-password", showPwd, setShowPwd, placeholder = "Jelszó" }) {
  return (
    <div className="relative">
      <input
        type={showPwd ? "text" : "password"}
        autoComplete={autoComplete}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls + " pr-11"}
        style={{ fontSize: 15 }}
        data-testid={testId}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShowPwd((v) => !v)}
        aria-label={showPwd ? "Jelszó elrejtése" : "Jelszó megjelenítése"}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center transition"
        style={{ color: "#7B7596" }}
      >
        {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

function SubmitBtn({ label, submitting, testId }) {
  return (
    <button
      type="submit"
      disabled={submitting}
      data-testid={testId}
      className="w-full inline-flex items-center justify-center gap-3 px-6 py-[15px] rounded-2xl font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
      style={{
        background: "linear-gradient(135deg, #6E5BFF 0%, #5e50f7 30%, #7C3AED 65%, #4F46E5 100%)",
        boxShadow: "0 20px 46px rgba(94,80,247,0.40), 0 6px 14px rgba(124,58,237,0.28), 0 0 0 1px rgba(255,255,255,0.28) inset",
        fontSize: 15,
      }}
    >
      {submitting ? "Feldolgozás…" : label}
    </button>
  );
}

function ErrorMsg({ children, testId = "login-modal-error" }) {
  if (!children) return null;
  return (
    <div className="mt-3 px-3.5 py-3 rounded-xl text-[13px] leading-snug" data-testid={testId} style={{ color: "#8A1A34", background: "rgba(251,113,133,0.10)", border: "1px solid rgba(251,113,133,0.22)" }}>
      {children}
    </div>
  );
}

function InfoMsg({ children, testId = "login-modal-info" }) {
  if (!children) return null;
  return (
    <div className="mt-3 px-3.5 py-3 rounded-xl text-[13px] leading-snug" data-testid={testId} style={{ color: "#22546C", background: "rgba(41,190,218,0.10)", border: "1px solid rgba(41,190,218,0.24)" }}>
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3 my-5" aria-hidden="true">
      <div className="flex-1 h-px" style={{ background: "linear-gradient(to right, transparent, #E5E0F5, transparent)" }} />
      <span className="text-[11px] tracking-[0.18em] uppercase text-[#9A93B6] font-semibold">vagy</span>
      <div className="flex-1 h-px" style={{ background: "linear-gradient(to right, transparent, #E5E0F5, transparent)" }} />
    </div>
  );
}

function TitleBlock({ title, subtitle, testId }) {
  return (
    <div>
      <h2 id="login-modal-title" data-testid={`${testId}-title`} className="font-heading font-bold text-[#1B1733] mb-2" style={{ fontSize: "clamp(22px, 2.4vw, 26px)", lineHeight: 1.2, letterSpacing: "-0.025em" }}>
        {title}
      </h2>
      <p data-testid={`${testId}-subtitle`} className="text-[#6B647F] mb-6" style={{ fontSize: 14, lineHeight: 1.55 }}>{subtitle}</p>
    </div>
  );
}

function LoginForm({ email, setEmail, password, setPassword, showPwd, setShowPwd, submit, submitting, error, info, onGoogle, goRegister, goForgot }) {
  return (
    <>
      <TitleBlock testId="login" title="Lépj be a MEX Magicbe" subtitle="Folytasd ott, ahol abbahagytad." />

      <button
        onClick={onGoogle}
        data-testid="login-modal-google-btn"
        type="button"
        className="w-full inline-flex items-center justify-center gap-3 px-6 py-[15px] rounded-2xl font-bold text-[#1B1733] bg-white transition-all hover:-translate-y-0.5"
        style={{
          border: "1px solid #E5E0F5",
          boxShadow: "0 12px 30px rgba(60,52,137,0.10), 0 2px 4px rgba(60,52,137,0.06)",
          fontSize: 15,
        }}
      >
        <GoogleLogo size={18} weight="bold" />
        Belépés Google-fiókkal
      </button>

      <Divider />

      <form onSubmit={submit} className="space-y-3" data-testid="login-modal-password-form">
        <EmailInput value={email} onChange={setEmail} testId="login-modal-email" autoFocus={!email} />
        <PasswordInput value={password} onChange={setPassword} testId="login-modal-password" showPwd={showPwd} setShowPwd={setShowPwd} />

        <div className="flex justify-end -mt-1">
          <button type="button" onClick={goForgot} data-testid="login-modal-forgot-link" className="text-[13px] font-medium text-[#5e50f7] hover:text-[#4a3fd0] transition">
            Elfelejtetted a jelszavad?
          </button>
        </div>

        <SubmitBtn label="Belépés" submitting={submitting} testId="login-modal-submit" />
        <ErrorMsg>{error}</ErrorMsg>
        <InfoMsg>{info}</InfoMsg>
      </form>

      <div className="mt-6 pt-5 border-t border-[#EFEAFA] text-center">
        <div className="text-[13.5px] text-[#6B647F]">
          Nincs még fiókod?{" "}
          <button type="button" onClick={goRegister} data-testid="login-modal-register-link" className="font-semibold text-[#5e50f7] hover:text-[#4a3fd0] transition">
            Regisztráció
          </button>
        </div>
        <div className="mt-3 text-[11.5px] text-[#9A93B6] leading-snug">
          A MEX Magic jelenleg csak meghívott felhasználók számára érhető el.
        </div>
      </div>
    </>
  );
}

function RegisterForm({ firstName, setFirstName, email, setEmail, password, setPassword, confirmPassword, setConfirmPassword, showPwd, setShowPwd, submit, submitting, error, info, onGoogle, goLogin }) {
  return (
    <>
      <TitleBlock testId="register" title="Fiók létrehozása" subtitle="Ha erre az email címre kaptál meghívót, Google-lel automatikusan létrehozzuk a fiókodat és beléptetünk." />

      <button
        onClick={onGoogle}
        data-testid="register-modal-google-btn"
        type="button"
        className="w-full inline-flex items-center justify-center gap-3 px-6 py-[15px] rounded-2xl font-bold text-[#1B1733] bg-white transition-all hover:-translate-y-0.5"
        style={{
          border: "1px solid #E5E0F5",
          boxShadow: "0 12px 30px rgba(60,52,137,0.10), 0 2px 4px rgba(60,52,137,0.06)",
          fontSize: 15,
        }}
      >
        <GoogleLogo size={18} weight="bold" />
        Folytatás Google-fiókkal
      </button>

      <Divider />

      <form onSubmit={submit} className="space-y-3" data-testid="register-modal-form">
        <div>
          <label htmlFor="register-first-name" className="block text-[12px] font-semibold text-[#433C66] mb-1.5">
            Keresztnév
          </label>
          <input
            id="register-first-name"
            type="text"
            autoComplete="given-name"
            required
            autoFocus
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Pl. Anna"
            className={inputCls}
            style={{ fontSize: 15 }}
            data-testid="register-modal-first-name"
            maxLength={60}
          />
        </div>
        <EmailInput value={email} onChange={setEmail} testId="register-modal-email" />
        <PasswordInput value={password} onChange={setPassword} testId="register-modal-password" showPwd={showPwd} setShowPwd={setShowPwd} autoComplete="new-password" placeholder="Jelszó" />
        <PasswordInput value={confirmPassword} onChange={setConfirmPassword} testId="register-modal-password-confirm" showPwd={showPwd} setShowPwd={setShowPwd} autoComplete="new-password" placeholder="Jelszó megerősítése" />
        <p className="text-[11.5px] text-[#7B7596] leading-snug pt-1">
          A jelszónak legalább <b>10 karakterből</b> kell állnia, és tartalmaznia kell kisbetűt, nagybetűt, számot és speciális karaktert.
        </p>
        <SubmitBtn label="Fiók létrehozása" submitting={submitting} testId="register-modal-submit" />
        <ErrorMsg>{error}</ErrorMsg>
        <InfoMsg>{info}</InfoMsg>
      </form>

      <div className="mt-6 pt-5 border-t border-[#EFEAFA] text-center">
        <div className="text-[13.5px] text-[#6B647F]">
          Már van fiókod?{" "}
          <button type="button" onClick={goLogin} data-testid="register-modal-login-link" className="font-semibold text-[#5e50f7] hover:text-[#4a3fd0] transition">
            Belépés
          </button>
        </div>
        <div className="mt-3 text-[11.5px] text-[#9A93B6] leading-snug">
          A MEX Magic jelenleg csak meghívott felhasználók számára érhető el.
        </div>
      </div>
    </>
  );
}

function ForgotForm({ email, setEmail, submit, submitting, error, info, goLogin }) {
  return (
    <>
      <TitleBlock testId="forgot" title="Elfelejtetted a jelszavad?" subtitle="Add meg a fiókodhoz tartozó e-mail címet, és küldünk egy jelszó-visszaállító linket." />

      <form onSubmit={submit} className="space-y-3" data-testid="forgot-modal-form">
        <EmailInput value={email} onChange={setEmail} testId="forgot-modal-email" autoFocus />
        <SubmitBtn label="Reset link kérése" submitting={submitting} testId="forgot-modal-submit" />
        <ErrorMsg>{error}</ErrorMsg>
        <InfoMsg>{info}</InfoMsg>
      </form>

      <div className="mt-6 pt-5 border-t border-[#EFEAFA] text-center">
        <button type="button" onClick={goLogin} data-testid="forgot-modal-back-link" className="text-[13.5px] font-semibold text-[#5e50f7] hover:text-[#4a3fd0] transition">
          Vissza a belépéshez
        </button>
      </div>
    </>
  );
}

function ResetForm({ password, setPassword, confirmPassword, setConfirmPassword, showPwd, setShowPwd, submit, submitting, error, info }) {
  return (
    <>
      <TitleBlock testId="reset" title="Új jelszó beállítása" subtitle="Válassz egy erős új jelszót. Utána automatikusan beléptetünk." />

      <form onSubmit={submit} className="space-y-3" data-testid="reset-modal-form">
        <PasswordInput value={password} onChange={setPassword} testId="reset-modal-password" showPwd={showPwd} setShowPwd={setShowPwd} autoComplete="new-password" placeholder="Új jelszó" />
        <PasswordInput value={confirmPassword} onChange={setConfirmPassword} testId="reset-modal-password-confirm" showPwd={showPwd} setShowPwd={setShowPwd} autoComplete="new-password" placeholder="Új jelszó megerősítése" />
        <p className="text-[11.5px] text-[#7B7596] leading-snug pt-1">
          A jelszónak legalább <b>10 karakterből</b> kell állnia, és tartalmaznia kell kisbetűt, nagybetűt, számot és speciális karaktert.
        </p>
        <SubmitBtn label="Jelszó beállítása" submitting={submitting} testId="reset-modal-submit" />
        <ErrorMsg>{error}</ErrorMsg>
        <InfoMsg>{info}</InfoMsg>
      </form>
    </>
  );
}

function DeniedBlock({ denied, onRetry }) {
  const isInactive = denied?.kind === "inactive";
  const isMismatch = denied?.kind === "google_email_mismatch";
  return (
    <div data-testid="login-modal-denied">
      <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: isInactive ? "rgba(251,113,133,0.12)" : "rgba(94,80,247,0.10)", color: isInactive ? "#E11D48" : "#5e50f7" }}>
        <WarningCircle size={26} weight="fill" />
      </div>
      <h2 className="font-heading font-bold text-[#1B1733] mb-3" style={{ fontSize: "22px", lineHeight: 1.2, letterSpacing: "-0.02em" }}>
        {isInactive ? "Hozzáférés inaktív" : isMismatch ? "Másik Google-fiókot választottál" : "Nincs még meghívásod"}
      </h2>
      <p className="text-[#433C66] mb-7" style={{ fontSize: "14.5px", lineHeight: 1.6 }}>{denied?.message}</p>
      <button type="button" onClick={onRetry} data-testid="login-modal-denied-back" className="w-full inline-flex items-center justify-center gap-2 px-6 py-[14px] rounded-2xl font-semibold transition" style={{ background: "rgba(94,80,247,0.08)", color: "#5e50f7", border: "1px solid rgba(94,80,247,0.18)", fontSize: "14.5px" }}>
        {isMismatch ? "Választok másik fiókot" : "Vissza a kezdőlapra"}
      </button>
    </div>
  );
}
